import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Button,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ConfirmDialog from "../ConfirmDialog";
import { updateDataTableSettings } from "../../actions";
import { styled } from "@mui/material";
import {
  buttonStyles,
  TableContainerStyle,
  CircularProgressStyles,
  TableHeadStyle,
  TableStyle,
} from "./DataTableStyle";
import { caseInsensitiveIteree } from "../../utils/helpers";

const ACTIONS_COLUMN = {
  field: "actions",
  label: "Actions",
  isSortable: false,
  align: "center",
  sticky: true,
};

const StickyTableCell = styled(TableCell)(({ theme }) => ({
  position: "sticky",
  zIndex: 1000,
  borderLeft: `1px solid ${theme.palette.divider}`,
  backgroundColor: "#fafafa",
}));

const FontSize = [
  "0.6rem",
  "0.6rem",
  "0.6rem",
  "0.6rem",
  "0.875rem",
  "0.875rem",
];

const getField = (row, field) =>
  field.split(".").reduce((ret, subfield) => ret?.[subfield] || null, row);

const DataTable = ({
  dataList,
  total,
  tableName,
  columns,
  isFetchingData,
  onClickRow,
  onClickEdit,
  handleAddData,
  handleDeleteData,
  onContextMenu,
  options,
  actions = [],
  page,
  pageSize,
  orderBy,
  order,
  loading,
  rowsPerPageOptions,
  updateDataTableSettings,
}) => {
  const [data, setData] = useState([]);
  const {
    allowCreate = false,
    allowClick = false,
    allowContext = false,
  } = options;

  const [showConfirmation, setShowConfirmation] = useState({});
  if (actions.length > 0) {
    columns = [...columns, ACTIONS_COLUMN];
  }

  useEffect(() => {
    setData(
      _.chain(dataList)
        .orderBy([caseInsensitiveIteree(orderBy)], [order])
        .slice(page * pageSize, Math.min(total, (page + 1) * pageSize))
        .value()
    );
  }, [dataList, orderBy, order, total, page, pageSize]);

  const handleChangePage = (e, newPage) => {
    updateDataTableSettings(tableName, { page: newPage });
  };

  const handleChangeRowsPerPage = (e) => {
    updateDataTableSettings(tableName, { page: 0, pageSize: e.target.value });
  };

  const handleSorting = (property) => (e) => {
    const isAsc = property === orderBy && order === "asc";
    updateDataTableSettings(tableName, {
      order: isAsc ? "desc" : "asc",
      orderBy: property,
    });
  };

  return (
    <Paper
      sx={{
        width: [
          "320px",
          "320px",
          "calc(450px - 2rem)",
          "calc(450px- 2rem)",
          "calc(33vw - 3.5rem)",
          "calc(33vw - 3.5rem)",
        ],
        maxWidth: ["100vw", "350px", "450px", "450px", "544px", "544px"],
      }}
    >
      <div className={"flex flex-row justify-between"}>
        {(page || page === 0) && pageSize && handleChangePage ? (
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component={"div"}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            count={total}
            backIconButtonProps={{
              sx: { ...buttonStyles.infoButton },
            }}
            nextIconButtonProps={{
              sx: { ...buttonStyles.infoButton },
            }}
            sx={TableContainerStyle}
          />
        ) : null}

        {loading ? (
          <CircularProgress sx={CircularProgressStyles} />
        ) : tableName === "Collection Method" && allowCreate ? (
          <Tooltip
            title={`Add new ${tableName}`}
            sx={{
              float: "right",
            }}
          >
            <Button
              onClick={handleAddData}
              sx={{
                ...buttonStyles.infoButton,
                fontSize: FontSize,
                padding: 0,
              }}
            >
              Create
            </Button>
          </Tooltip>
        ) : (
          // <TrayFetchFilter tableName={tableName} />
          <></>
        )}
      </div>
      <TableContainer
        component={Paper}
        sx={{
          height: "80vh",
          overflow: "auto", // Enables both horizontal and vertical scrolling if content overflows
          flexGrow: 1,
        }}
      >
        <Table stickyHeader sx={TableStyle}>
          <TableHead sx={TableHeadStyle}>
            <TableRow sx={buttonStyles.tableHeadRow}>
              {columns.map((column) => {
                return column.sticky ? (
                  <StickyTableCell
                    key={column.id}
                    align="right"
                    sx={{ right: 0, ...column?.sx, zIndex: 1001 }}
                  >
                    {column.label}
                  </StickyTableCell>
                ) : (
                  <TableCell
                    key={column.field}
                    align={column.align}
                    sx={{ backgroundColor: "#fafafa", ...column?.sx }}
                  >
                    {column.isSortable && (
                      <TableSortLabel
                        active={orderBy === column.field}
                        direction={order}
                        onClick={handleSorting(column.field)}
                      />
                    )}
                    {column.label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => {
              return (
                <TableRow
                  hover
                  className={allowClick ? "cursor-pointer" : "cursor-default"}
                  onClick={allowClick ? () => onClickRow(row) : () => null}
                  onContextMenu={
                    allowContext ? (e) => onContextMenu(e, row) : () => null
                  }
                  key={`table-row-${row.id}`}
                >
                  {columns
                    .slice(0, actions.length > 0 ? -1 : undefined)
                    .map((col) => (
                      <TableCell
                        align={col.align}
                        key={`table-cell-row${row.id}-col${col.field}`}
                        sx={{
                          ...(col?.sx || {}),
                        }}
                      >
                        <Typography
                          sx={{
                            textOverflow: "ellipsis",
                            fontSize: FontSize,
                          }}
                        >
                          {col?.render
                            ? col.render(row)
                            : (col?.formatFn || ((s) => s))(
                              getField(row, col.field)
                            )}
                        </Typography>
                      </TableCell>
                    ))}
                  {actions.length > 0 ? (
                    <TableCell
                      key={actions.id}
                      align="right"
                      sx={{
                        right: 0,
                        display: "block",
                        position: "sticky",
                        zIndex: 1000,
                        borderLeft: `1px solid rgb(226, 232, 240)`,
                        width: [
                          "5rem",
                          "5rem",
                          "5rem",
                          "5rem",
                          "6.25rem",
                          "6.25rem",
                        ],
                        backgroundColor: "#fff",
                        padding: "0px !important",
                      }}
                    >
                      <div>
                        {actions.map((action) =>
                          action ? (
                            <span key={`action-${action.name}`}>
                              {action?.dialog ? (
                                <ConfirmDialog
                                  title={`${action.name} ${tableName}`}
                                  open={
                                    Array.isArray(
                                      showConfirmation?.[action.name]
                                    ) &&
                                    showConfirmation?.[action.name].length > 0
                                  }
                                  setOpen={() =>
                                    setShowConfirmation({
                                      ...showConfirmation,
                                      [action.name]: false,
                                    })
                                  }
                                  onConfirm={() => {
                                    showConfirmation?.[action.name]?.[1]();
                                    setShowConfirmation({});
                                  }}
                                >
                                  {showConfirmation?.[action.name]?.[0]}
                                </ConfirmDialog>
                              ) : null}
                              <Tooltip title={`${action.name} ${tableName}`}>
                                <IconButton
                                  onClick={() =>
                                    action?.dialog
                                      ? setShowConfirmation({
                                        ...showConfirmation,
                                        [action.name]: [
                                          action.dialog(row),
                                          () => action.handler(row),
                                        ],
                                      })
                                      : action.handler(row)
                                  }
                                  sx={{
                                    ...action.sx,
                                    padding: { xs: "0.5rem", lg: ".75rem" },
                                  }}
                                >
                                  <action.icon
                                    sx={{
                                      fontSize: [
                                        "1.25rem",
                                        "1.25rem",
                                        "1.25rem !important",
                                        "1.25rem !important",
                                        "1.5rem !important",
                                        "1.5rem !important",
                                      ],
                                      my: [
                                        ".28rem",
                                        ".28rem",
                                        ".28rem",
                                        ".28rem",
                                        "0rem",
                                        "0rem",
                                      ],
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </span>
                          ) : null
                        )}
                      </div>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper >
  );
};

const mapStateToProps = (state, { tableName }) => {
  if (tableName) {
    return state?.settings?.[tableName] || {};
  }
  return {};
};

export default connect(mapStateToProps, { updateDataTableSettings })(DataTable);
